/**
 * ADM-EDIT-USER.MODAL
 * Add or edit a new user
 */

import Modal from "@/components/modal"
import { connect } from "react-redux"
import { withTranslation, WithTranslation } from "react-i18next"
import { User, UserState } from "@/redux/user.types"
import { useEffect, useState } from "react"
import { store } from "@/index"
import { userCheckEmail, userDestroy, userGet, userInsert, userRemove, userStatus, userUpdate } from "@/redux/user.actions"
import { toast } from "react-toastify"
import { v4 as uuid } from "uuid"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVE_ERROR, STATUS_SAVED, STATUS_SAVING } from "@/redux/_status.types"
import ModalConfirm from "@/components/modal-confirm"
import PageLoader from "@/components/page-loader"
import { filterFetch, filterGet } from "@/redux/filter.actions"
import UserFilterInfoWidget from "@/widgets/user-filter-info.widget"
import UserOptionalInfoWidget from "@/widgets/user-optional-info.widget"
import { Session } from "@/redux/_session.types"
import { checkEmail } from "@/utils/validate-email.utils"
import TextInput from "@/components/text-input"
import ImagePicker from "@/components/image-picker"

interface StateProps extends WithTranslation {
  _session: Session
  user: UserState
}

interface OwnProps {
  allowFromSameAccount?: boolean //Allow to pick exinting user if it in the same account
  isSmall?: boolean //Display only necessary informations
  userToUpdate?: User //Detect if user to edit exist already (if not defined new user will be add)
  onClose: Function //On close trigger
  onDelete?: Function //On delete trigger
  onNext?: Function //On next trigger
}

type Props = StateProps & OwnProps

const MODAL_ASK_DELETE = "MODAL_ASK_DELETE"
const MODAL_ASK_DELETE_ADMIN = "MODAL_ASK_DELETE_ADMIN"
const MODAL_ASK_SAVE = "MODAL_ASK_SAVE"

function AdmEditUserModal(props: Props) {
  const { t } = props

  //Do not display all informations (risk module only or specified)
  const isSmall = props.isSmall ?? false

  //Current modal
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  // Status for email pro only
  const [emailStatus, setEmailStatus] = useState(STATUS_LOADING)
  const [emailError, setEmailError] = useState<string | null>(null)

  //Save changes
  const [isEdited, setIsEdited] = useState(false)

  //Loading
  const [isLoading, setIsLoading] = useState(isSmall ? false : true)

  //User to edit
  const [user, setUser] = useState<User>(props.userToUpdate ? new User(props.userToUpdate) : new User({ id: uuid() }))

  //Load filters at start
  useEffect(() => {
    load()

    async function load() {
      if (!isSmall) {
        const filters: any = await store.dispatch(filterFetch(true, false))
        store.dispatch(filterGet(filters.error ? [] : filters))
        setIsLoading(false)
      }
    }
  }, [])

  //Ask for deleting
  //If it is the connected user ask to delete account
  function askDelete() {
    setCurrentModal(user.id === props._session.userId ? MODAL_ASK_DELETE_ADMIN : MODAL_ASK_DELETE)
  }

  //Edit user
  function edit(key: string, value: any) {
    setUser(new User(Object.assign({}, user, { [key]: value })))
    setIsEdited(true)
  }

  //Edit attribute of the user
  function editAttribute(value, filterName) {
    const attributes = Object.assign({}, user.attributes, { [filterName]: value })
    edit("attributes", attributes)
  }

  //Close modal
  function closeModal() {
    if (isEdited) {
      setCurrentModal(MODAL_ASK_SAVE)
    } else {
      props.onClose()
    }
  }

  //Clic on next
  //CHeck that email adress is valid
  //Else
  //> if page email save the user adress and change page
  //> else close page
  async function next() {
    let emailValidationMessage = ""
    let userToSave = user

    const userInvalid = isSmall
      ? !user.email || !user.firstname.length || !user.lastname.length
      : !user.email && !user.firstname.length && !user.lastname.length

    if (userInvalid) {
      toast(t(isSmall ? "user_invalid_risks" : "user_invalid"), { type: "error" })
    } else {
      let skip = false
      if (user.email) {
        emailValidationMessage = await checkEmail(user.email, user.id, props.allowFromSameAccount)
        if (emailValidationMessage !== "OK" && emailValidationMessage !== "user_exist_in_account") {
          setEmailError(emailValidationMessage)
          skip = true
        } else if (emailValidationMessage === "user_exist_in_account") {
          //`user` is still a blank local object (random id, role null...) - fetch the real
          //existing account member and only overlay what was edited in this form, otherwise
          //userUpdate below would wipe the real user's role/id with this blank one
          const existing: any = await store.dispatch(userCheckEmail(user.email, user.id))
          if (existing?.user) {
            userToSave = new User({
              ...existing.user,
              firstname: user.firstname,
              lastname: user.lastname,
              email: user.email
            })
            setUser(userToSave)
          }
        }
      }

      if (!skip) {
        //Insert user
        store.dispatch(userStatus(STATUS_SAVING))
        const response: any =
          props.userToUpdate || emailValidationMessage === "user_exist_in_account"
            ? await store.dispatch(userUpdate(userToSave, true))
            : await store.dispatch(userInsert(userToSave))
        store.dispatch(userStatus(STATUS_SAVED))

        //Update store
        //Different behavior if edit or insert
        if (!response.error) {
          if (props.userToUpdate) {
            store.dispatch(userGet(props.user.list.map((x) => (x.id === userToSave.id ? userToSave : x))))
          } else {
            store.dispatch(userGet([userToSave].concat(props.user.list)))
          }

          //Close popup
          if (props.onNext) props.onNext(userToSave)
        }
      }
    }
  }

  //Remove user
  async function remove() {
    store.dispatch(userStatus(STATUS_SAVING))
    const response: any = await store.dispatch(userDestroy(user.id))
    store.dispatch(userStatus(STATUS_SAVED))
    if (!response.error) {
      if (props.onDelete) props.onDelete(user.id)
      store.dispatch(userRemove(user.id))
      props.onClose()
    }
  }

  function editImage(image: boolean) {
    edit("options", Object.assign({}, user.options, { image }))
  }

  // Check if email is valid (pro email only)
  async function validateEmail() {
    setEmailStatus(STATUS_SAVING)
    if (user.email === "") {
      setEmailError(null)
      setEmailStatus(STATUS_SAVED)
      store.dispatch(userStatus(STATUS_SAVED))
    } else {
      const response: string = await checkEmail(user.email, user.id, props.allowFromSameAccount)
      if (response === "OK" || response === "user_exist_in_account") {
        setEmailError(null)
        setEmailStatus(STATUS_SAVED)
        store.dispatch(userStatus(STATUS_SAVED))
      } else {
        setEmailError(response)
        setEmailStatus(STATUS_SAVE_ERROR)
        store.dispatch(userStatus(STATUS_SAVE_ERROR))
      }
    }
  }

  return (
    <Modal
      isLarge
      isCloseButtonVisible
      status={props.user.status}
      onNext={emailError ? undefined : next}
      onDelete={props.userToUpdate ? askDelete : undefined}
      onClose={closeModal}
      disableClickOutside
      disableKeyEvents
      title={props.userToUpdate ? t("user_edit") : t("user_add")}>
      {currentModal === MODAL_ASK_DELETE && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={() => remove()}
          text={t("user_ask_delete", {
            user: user.username
          })}
          status={props.user.status}
          textBold={t("utils_next_ask")}
        />
      )}

      {currentModal === MODAL_ASK_DELETE_ADMIN && (
        <Modal onClose={() => setCurrentModal(null)} isCloseButtonVisible>
          <p>
            <b>{t("utils_delete_self")}</b>
          </p>
          <div className="height-20" />
          <div>{t("utils_delete_self_help")}</div>
          <div className="height-20" />
        </Modal>
      )}

      {currentModal === MODAL_ASK_SAVE && (
        <ModalConfirm
          onNo={() => props.onClose()}
          status={props.user.status}
          onYes={next}
          textBold={t("utils_ask_confirm_edits")}
        />
      )}

      <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
        {!isSmall && (
          <p>
            <b>{t("utils_informations")}</b>
          </p>
        )}

        <div className="flex">
          <div className="flex-1">
            <div className="flex">
              <TextInput
                onChange={(e) => edit("firstname", e.value)}
                isFullWidth
                title={t("user_firstname")}
                value={user.firstname}
              />

              <div className="width-20" />

              <TextInput
                onChange={(e) => edit("lastname", e.value)}
                isFullWidth
                title={t("user_lastname")}
                value={user.lastname}
              />
            </div>

            <div className="flex" style={{ marginTop: 10, gap: 20 }}>
              <TextInput
                status={emailStatus}
                onBlur={validateEmail}
                onChange={(e: any) => edit("email", e.value)}
                error={!!emailError}
                title={t("user_email")}
                value={user.email}
                isFullWidth
              />

              {emailError && (
                <div className="red-t" style={{ marginTop: 42 }}>
                  <b>{t(emailError)}</b>
                </div>
              )}
            </div>
          </div>

          {
            /** for risk widget set image for the user */
            isSmall && (
              <div className="mt-[12px]">
                <ImagePicker
                  id={user.id}
                  onDelete={() => editImage(false)}
                  onSave={() => editImage(true)}
                  imageId={user.options.image ? (user.options.imageId ? user.options.imageId : user.id) : null}
                  model="Users"
                />
              </div>
            )
          }
        </div>

        <div className="height-40" />

        {!isSmall && (
          <div>
            <UserFilterInfoWidget
              attributes={user.attributes}
              username={user.username}
              isEditable
              onEdit={(key, value) => editAttribute(value, key)}
            />

            <div className="height-40" />

            <UserOptionalInfoWidget
              birthDate={user.birthDate}
              emailPerso={user.emailPerso ? user.emailPerso : ""}
              companyWelcomeDate={user.companyWelcomeDate}
              phone={user.phone}
              address={user.address}
              language={user.language}
              gender={user.gender}
              genderLabel={user.genderLabel}
              onEdit={edit}
            />

            <div className="height-20" />
          </div>
        )}
      </PageLoader>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  user: state.user
})

export default connect(mapStateToProps)(withTranslation()(AdmEditUserModal))
